import React, { useMemo, memo } from 'react';

/**
 * HOC que estabiliza las propiedades relacionadas con i18n para prevenir re-renders innecesarios
 *
 * Esta función combina React.memo con una función de comparación personalizada que ignora
 * específicamente las props que cambian frecuentemente pero rara vez afectan el renderizado real.
 *
 * @param {React.Component} Component - El componente a envolver
 * @returns {React.Component} - Componente envuelto con props de i18n estabilizadas
 */
const withStableI18nProps = (Component) => {
  // Función de comparación para React.memo que ignora tReady e i18n
  const arePropsEqual = (prevProps, nextProps) => {
    // Extraer las props problemáticas
    const { tReady: prevTReady, i18n: prevI18n, ...prevRest } = prevProps;
    const { tReady: nextTReady, i18n: nextI18n, ...nextRest } = nextProps;

    // Comparación de las demás props
    return Object.keys(prevRest).every((key) => {
      // Verificar caso especial para la función t que también debe ser estable
      if (key === 't' && prevRest.t && nextRest.t) {
        return true; // La función t será reemplazada por nuestra versión estable
      }
      return Object.is(prevRest[key], nextRest[key]);
    });
  };

  // Componente interno que implementa la estabilización de props
  const WithStableI18nProps = (props) => {
    // Extraer props relacionadas con i18n
    const { i18n, tReady, t, ...otherProps } = props;

    // Crear una versión estable de la función t
    const stableT = useMemo(() => {
      if (!t) return undefined;
      return (key, options) => t(key, options);
    }, []);

    // Renderizar el componente con las props estabilizadas
    return <Component {...otherProps} t={stableT} />;
  };

  // Establecer un nombre para depuración
  WithStableI18nProps.displayName = `WithStableI18nProps(${Component.displayName || Component.name || 'Component'})`;

  // Aplicar memo con la función de comparación personalizada
  return memo(WithStableI18nProps, arePropsEqual);
};

export default withStableI18nProps;
